import GeoJSON from 'ol/format/GeoJSON';
import type Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import proj4 from 'proj4';
import type { Database, SqlJsStatic, SqlValue } from 'sql.js';
import type { AttributeValue, VectorFeatureRecord, VectorLayerModel } from '../core/types';
import { createGenericLayer } from '../domain/layers';

export interface GeoPackageFieldInfo {
  readonly name: string;
  readonly type: string;
  readonly nullable: boolean;
}

export interface GeoPackageLayerInfo {
  readonly tableName: string;
  readonly displayName: string;
  readonly geometryColumn: string;
  readonly geometryType: string;
  readonly featureCount: number;
  readonly srsId: number;
  readonly sourceCrs: string;
  readonly srsDefinition: string;
  readonly fields: readonly GeoPackageFieldInfo[];
}

export interface GeoPackageInspection {
  readonly fileName: string;
  readonly layers: readonly GeoPackageLayerInfo[];
}

export class GeoPackageImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GeoPackageImportError';
  }
}

type JsonCoordinates = number[] | JsonCoordinates[];
interface JsonGeometry {
  readonly type: string;
  readonly coordinates?: JsonCoordinates;
  readonly geometries?: readonly JsonGeometry[];
}

let sqlJsPromise: Promise<SqlJsStatic> | null = null;

async function loadSqlJs(): Promise<SqlJsStatic> {
  if (!sqlJsPromise) {
    sqlJsPromise = (async () => {
      const initSqlJs = (await import('sql.js')).default;
      if (typeof process !== 'undefined' && process.versions?.node) return initSqlJs();
      const wasmUrl = (await import('sql.js/dist/sql-wasm.wasm?url')).default;
      return initSqlJs({ locateFile: () => wasmUrl });
    })();
  }
  return sqlJsPromise;
}

async function openDatabase(file: File): Promise<Database> {
  try {
    const SQL = await loadSqlJs();
    return new SQL.Database(new Uint8Array(await file.arrayBuffer()));
  } catch {
    throw new GeoPackageImportError(`No se pudo abrir ${file.name} como GeoPackage válido.`);
  }
}

const quoteIdentifier = (value: string): string => `"${value.replace(/"/g, '""')}"`;

function queryRows(db: Database, sql: string, params: readonly SqlValue[] = []): Record<string, SqlValue>[] {
  const statement = db.prepare(sql);
  const rows: Record<string, SqlValue>[] = [];
  try {
    if (params.length) statement.bind([...params]);
    while (statement.step()) rows.push(statement.getAsObject());
  } finally {
    statement.free();
  }
  return rows;
}

const textValue = (value: SqlValue | undefined): string => typeof value === 'string' ? value : value == null ? '' : String(value);
const numberValue = (value: SqlValue | undefined): number => typeof value === 'number' ? value : Number(value);

function inspectDatabase(db: Database, fileName: string): GeoPackageInspection {
  const requiredTables = queryRows(db, "SELECT name FROM sqlite_master WHERE type='table' AND name IN ('gpkg_contents','gpkg_geometry_columns','gpkg_spatial_ref_sys')");
  if (requiredTables.length !== 3) throw new GeoPackageImportError(`${fileName} no contiene las tablas obligatorias de un GeoPackage.`);
  const metadata = queryRows(db, `
    SELECT gc.table_name, gc.column_name, gc.geometry_type_name, gc.srs_id,
      COALESCE(c.identifier, gc.table_name) AS identifier,
      s.organization, s.organization_coordsys_id, s.definition
    FROM gpkg_geometry_columns gc
    JOIN gpkg_contents c ON c.table_name = gc.table_name AND c.data_type = 'features'
    JOIN gpkg_spatial_ref_sys s ON s.srs_id = gc.srs_id
    ORDER BY gc.table_name
  `);
  if (!metadata.length) throw new GeoPackageImportError('El GeoPackage no contiene capas vectoriales.');
  const layers = metadata.map((row): GeoPackageLayerInfo => {
    const tableName = textValue(row.table_name);
    const fields = queryRows(db, `PRAGMA table_info(${quoteIdentifier(tableName)})`)
      .filter((field) => textValue(field.name) !== textValue(row.column_name))
      .map((field) => ({ name: textValue(field.name), type: textValue(field.type), nullable: numberValue(field.notnull) !== 1 }));
    const count = queryRows(db, `SELECT COUNT(*) AS count FROM ${quoteIdentifier(tableName)}`)[0];
    const organization = textValue(row.organization).toUpperCase();
    const organizationCode = numberValue(row.organization_coordsys_id);
    const srsId = numberValue(row.srs_id);
    return {
      tableName,
      displayName: textValue(row.identifier) || tableName,
      geometryColumn: textValue(row.column_name),
      geometryType: textValue(row.geometry_type_name).toUpperCase(),
      featureCount: numberValue(count?.count),
      srsId,
      sourceCrs: organization === 'EPSG' && Number.isFinite(organizationCode) ? `EPSG:${organizationCode}` : `SRS:${srsId}`,
      srsDefinition: textValue(row.definition),
      fields,
    };
  });
  return { fileName, layers };
}

export async function inspectGeoPackage(file: File): Promise<GeoPackageInspection> {
  const db = await openDatabase(file);
  try {
    return inspectDatabase(db, file.name);
  } catch (error) {
    if (error instanceof GeoPackageImportError) throw error;
    throw new GeoPackageImportError(`No se pudieron inspeccionar las capas de ${file.name}.`);
  } finally {
    db.close();
  }
}

class BinaryReader {
  private offset: number;
  constructor(private readonly view: DataView, offset = 0) { this.offset = offset; }
  uint8(): number { return this.view.getUint8(this.offset++); }
  uint32(littleEndian: boolean): number { const value = this.view.getUint32(this.offset, littleEndian); this.offset += 4; return value; }
  int32(littleEndian: boolean): number { const value = this.view.getInt32(this.offset, littleEndian); this.offset += 4; return value; }
  float64(littleEndian: boolean): number { const value = this.view.getFloat64(this.offset, littleEndian); this.offset += 8; return value; }
}

interface GeometryTypeInfo { base: number; dimensions: number; hasSrid: boolean }

function geometryTypeInfo(rawType: number): GeometryTypeInfo {
  const unsigned = rawType >>> 0;
  const ewkbDimensions = 2 + ((unsigned & 0x80000000) !== 0 ? 1 : 0) + ((unsigned & 0x40000000) !== 0 ? 1 : 0);
  const clean = unsigned & 0x0fffffff;
  const family = Math.floor(clean / 1000);
  return {
    base: clean % 1000,
    dimensions: family >= 1 && family <= 3 ? 2 + (family === 3 ? 2 : 1) : ewkbDimensions,
    hasSrid: (unsigned & 0x20000000) !== 0,
  };
}

function readPosition(reader: BinaryReader, littleEndian: boolean, dimensions: number): number[] {
  const ordinates = Array.from({ length: dimensions }, () => reader.float64(littleEndian));
  return ordinates.slice(0, 2);
}

function readWkbGeometry(reader: BinaryReader): JsonGeometry | null {
  const byteOrder = reader.uint8();
  if (byteOrder !== 0 && byteOrder !== 1) throw new GeoPackageImportError('La geometría contiene un orden de bytes WKB inválido.');
  const littleEndian = byteOrder === 1;
  const type = geometryTypeInfo(reader.uint32(littleEndian));
  if (type.hasSrid) reader.uint32(littleEndian);
  if (type.base === 1) {
    const position = readPosition(reader, littleEndian, type.dimensions);
    return position.every(Number.isNaN) ? null : { type: 'Point', coordinates: position };
  }
  if (type.base === 2) {
    const count = reader.uint32(littleEndian);
    return { type: 'LineString', coordinates: Array.from({ length: count }, () => readPosition(reader, littleEndian, type.dimensions)) };
  }
  if (type.base === 3) {
    const rings = reader.uint32(littleEndian);
    return { type: 'Polygon', coordinates: Array.from({ length: rings }, () => {
      const count = reader.uint32(littleEndian);
      return Array.from({ length: count }, () => readPosition(reader, littleEndian, type.dimensions));
    }) };
  }
  if (type.base >= 4 && type.base <= 6) {
    const count = reader.uint32(littleEndian);
    const parts = Array.from({ length: count }, () => readWkbGeometry(reader)).filter((part): part is JsonGeometry => Boolean(part));
    const expected = type.base === 4 ? 'Point' : type.base === 5 ? 'LineString' : 'Polygon';
    if (parts.some((part) => part.type !== expected || !part.coordinates)) throw new GeoPackageImportError('La geometría WKB multiparte contiene componentes incompatibles.');
    return { type: type.base === 4 ? 'MultiPoint' : type.base === 5 ? 'MultiLineString' : 'MultiPolygon', coordinates: parts.map((part) => part.coordinates!) };
  }
  if (type.base === 7) {
    const count = reader.uint32(littleEndian);
    return { type: 'GeometryCollection', geometries: Array.from({ length: count }, () => readWkbGeometry(reader)).filter((part): part is JsonGeometry => Boolean(part)) };
  }
  throw new GeoPackageImportError(`Tipo de geometría WKB no soportado: ${type.base}.`);
}

export function decodeGeoPackageGeometry(value: Uint8Array): { geometry: JsonGeometry | null; srsId: number } {
  if (value.byteLength < 8) throw new GeoPackageImportError('La geometría GeoPackage está truncada.');
  const view = new DataView(value.buffer, value.byteOffset, value.byteLength);
  if (view.getUint8(0) !== 0x47 || view.getUint8(1) !== 0x50) throw new GeoPackageImportError('La geometría no contiene una cabecera GeoPackage válida.');
  const flags = view.getUint8(3);
  const littleEndian = (flags & 1) === 1;
  const envelopeType = (flags >> 1) & 7;
  const envelopeValues = envelopeType === 0 ? 0 : envelopeType === 1 ? 4 : envelopeType === 2 || envelopeType === 3 ? 6 : envelopeType === 4 ? 8 : -1;
  if (envelopeValues < 0) throw new GeoPackageImportError('La cabecera GeoPackage declara un envelope inválido.');
  const offset = 8 + envelopeValues * 8;
  if (offset >= value.byteLength) throw new GeoPackageImportError('La geometría GeoPackage está truncada.');
  const srsId = view.getInt32(4, littleEndian);
  return { geometry: readWkbGeometry(new BinaryReader(view, offset)), srsId };
}

const transformCoordinates = (coordinates: JsonCoordinates, forward: (coordinate: [number, number]) => [number, number]): JsonCoordinates => {
  if (typeof coordinates[0] === 'number') {
    const position = coordinates as number[];
    const result = forward([position[0]!, position[1]!]);
    if (!result.every(Number.isFinite)) throw new GeoPackageImportError('La reproyección produjo coordenadas inválidas. Verifica el CRS de la capa.');
    return result;
  }
  return (coordinates as JsonCoordinates[]).map((part) => transformCoordinates(part, forward));
};

const transformGeometry = (geometry: JsonGeometry | null, forward: (coordinate: [number, number]) => [number, number]): JsonGeometry | null => {
  if (!geometry) return null;
  if (geometry.type === 'GeometryCollection') return { ...geometry, geometries: geometry.geometries?.map((part) => transformGeometry(part, forward)!).filter(Boolean) };
  return geometry.coordinates ? { ...geometry, coordinates: transformCoordinates(geometry.coordinates, forward) } : geometry;
};

proj4.defs('EPSG:32616', '+proj=utm +zone=16 +datum=WGS84 +units=m +no_defs +type=crs');

function projectionFor(layer: GeoPackageLayerInfo) {
  const definition = ['EPSG:4326', 'EPSG:3857', 'EPSG:32616'].includes(layer.sourceCrs) ? layer.sourceCrs : layer.srsDefinition;
  if (!definition || /^(undefined|unknown)$/i.test(definition)) throw new GeoPackageImportError(`No se pudo interpretar el CRS de ${layer.tableName} (SRS ${layer.srsId}).`);
  try {
    const converter = proj4(definition, 'EPSG:3857');
    converter.forward(layer.sourceCrs === 'EPSG:4326' ? [0, 0] : [500000, 1300000]);
    return converter;
  } catch {
    throw new GeoPackageImportError(`No se pudo interpretar el CRS de ${layer.tableName} (SRS ${layer.srsId}).`);
  }
}

const normalizeAttribute = (value: SqlValue, declaredType: string): AttributeValue => {
  if (value === null) return null;
  if (typeof value === 'number' && /BOOL/i.test(declaredType)) return value !== 0;
  if (typeof value === 'number' || typeof value === 'string') return value;
  return Array.from(value as Uint8Array).join(',');
};

let importSequence = 0;
const slug = (value: string): string => value.normalize('NFKD').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase() || 'geopackage';

function importTable(db: Database, file: File, layer: GeoPackageLayerInfo): VectorLayerModel {
  const converter = projectionFor(layer);
  const declaredTypes = new Map(layer.fields.map((field) => [field.name, field.type]));
  const statement = db.prepare(`SELECT * FROM ${quoteIdentifier(layer.tableName)}`);
  const geoJson = new GeoJSON();
  const layerId = `layer:gpkg:${slug(file.name)}:${slug(layer.tableName)}:${++importSequence}`;
  const records: VectorFeatureRecord[] = [];
  try {
    let index = 0;
    while (statement.step()) {
      const row = statement.getAsObject();
      const binary = row[layer.geometryColumn];
      if (!(binary instanceof Uint8Array)) continue;
      const decoded = decodeGeoPackageGeometry(binary);
      if (decoded.srsId !== layer.srsId) throw new GeoPackageImportError(`${layer.tableName} contiene una geometría con SRS ${decoded.srsId}, distinto del SRS ${layer.srsId} declarado.`);
      const geometry = transformGeometry(decoded.geometry, (coordinate) => converter.forward(coordinate) as [number, number]);
      if (!geometry) continue;
      const attributes: Record<string, AttributeValue> = {};
      Object.entries(row).forEach(([name, value]) => {
        if (name !== layer.geometryColumn) attributes[name] = normalizeAttribute(value, declaredTypes.get(name) ?? '');
      });
      const id = `${layerId}/feature:${index++}`;
      const feature = geoJson.readFeature({ type: 'Feature', geometry, properties: attributes }) as Feature<Geometry>;
      feature.set('layerId', layerId, true);
      feature.set('genericFeatureId', id, true);
      records.push({ id, feature, attributes });
    }
  } catch (error) {
    if (error instanceof GeoPackageImportError) throw error;
    throw new GeoPackageImportError(`No se pudo leer la capa ${layer.tableName}.`);
  } finally {
    statement.free();
  }
  return createGenericLayer({
    id: layerId,
    name: layer.displayName,
    sourceName: `${file.name} · ${layer.tableName}`,
    sourceFormat: 'GPKG',
    sourceCrs: layer.sourceCrs,
    assumedCrs: false,
    records,
  });
}

export async function importGeoPackage(file: File, tableNames: readonly string[]): Promise<VectorLayerModel[]> {
  if (!tableNames.length) throw new GeoPackageImportError('Selecciona al menos una capa vectorial del GeoPackage.');
  const db = await openDatabase(file);
  try {
    const inspection = inspectDatabase(db, file.name);
    const selected = tableNames.map((name) => inspection.layers.find((layer) => layer.tableName === name));
    if (selected.some((layer) => !layer)) throw new GeoPackageImportError('La selección contiene una capa que no existe en el GeoPackage.');
    return selected.map((layer) => importTable(db, file, layer!));
  } finally {
    db.close();
  }
}
